import { i18n } from '@lingui/core'
import { msg, t, Trans } from '@lingui/macro'
import { Add, Delete } from '@mui/icons-material'
import {
    Divider,
    Icon,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    MenuItem,
    Table,
    TableCell,
    TableRow,
    Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Grid from '@mui/material/Grid'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import { makeStyles } from '@mui/styles'
import { deleteObjective } from 'app/services/sfAuth/sfData/sfObjectives'
import {
    CBTI_OBJECTIVE_RECORD_TYPE,
    CBTI_RECORD_TYPE,
    CHGF_CB_OBJECTIVE_RECORD_TYPE,
    CHGF_CB_RECORD_TYPE,
    CHGF_PP_OBJECTIVE_RECORD_TYPE,
    CHGF_PP_RECORD_TYPE,
    CHGF_RI_OBJECTIVE_RECORD_TYPE,
    CHGF_RI_RECORD_TYPE,
    STF_LP_OBJECTIVE_RECORD_TYPE,
    STF_LP_RECORD_TYPE,
    STF_SIP_OBJECTIVE_RECORD_TYPE,
    STF_SIP_RECORD_TYPE
} from 'app/services/sfAuth/sfData/sfOpportunity'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { FormHelptext } from 'app/views/forms/common/FormHelpText'
import { requiredTrans } from 'app/views/forms/formTranslations'
import { purgeInvalidPDFSignFromString } from 'app/views/forms/pdf-components/formPdfUtils'
import { FieldArray, useFormikContext } from 'formik'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React from 'react'
import { useSelector } from 'react-redux'
import { languages, myI18n } from 'translation/I18nConnectedProvider'
import * as Yup from 'yup'
import { FormErrorLabel } from '../../../../common/labels/FormErrorLabel'
import { parseFormLabelText } from '../../../common/Common'
import { endEditingField } from '../../../multiuser/grpcMultiuserEdit'

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    padding: theme.spacing(2)
    // width: 800
  },
  dataPicker: {
    paddingLeft: 15,
    paddingRight: 15,
    paddingTop: 15,
    width: '100%',
    marginBottom: 16
  },
  tcSpan4: {
    paddingLeft: 50,
    paddingTop: 25,
    paddingBottom: 25,
    paddingRight: 35
  },
  tr: {
    borderBottom: 'solid 1px #c1c1c1',
    borderTop: 'solid 0px #ffffff'
  },
  tHeaderText: {
    textShadow: '2px 2px 0px rgba(63,107,169, 0.15)',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  divCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paragraph: {
    marginBottom: '1rem'
  }
})
const useStyles = makeStyles(styles)

export const formObjectivesValueToText = (v) => {
  return languages.reduce((acc, lang) => {
    if (lang === 'en') {
      acc[lang] = (
        <div>
          <Trans>Data is too complex to show objectives!</Trans>
          <div style={{ color: '#e0a912' }}>
            <Trans>
              Overriding will ony save changes to already added objectives. It
              will not delete any newly added milestones. Manual cleanup may be
              required.
            </Trans>
          </div>
        </div>
      )
    } else {
      acc[lang] = <Trans>Data is too complex to show differences!</Trans>
    }
    return acc
  }, {})
}

export const FormObjectivesExtractKey = ({
  saveMap,
  sfObject,
  value,
  connectedObjectId,
  additionalSFInfo,
  appConfigurations
}) => {
  const { recordTypes } = additionalSFInfo
  const fundType = sfObject.RecordType.Name

  // For objective report type (Needed for creating one) we use value selected on funding stream the opoprtunity was created under
  const config = appConfigurations.find(
    (config) => config.id === sfObject.Funding_Stream__r.Id
  )
  let objectiveRecordType = config?.objectiveRecordType

  if (!objectiveRecordType) {
    switch (fundType) {
      case CBTI_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[CBTI_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      case STF_LP_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[STF_LP_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      case STF_SIP_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[STF_SIP_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      case CHGF_CB_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[CHGF_CB_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      case CHGF_PP_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[CHGF_PP_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      case CHGF_RI_RECORD_TYPE:
        objectiveRecordType =
          recordTypes[CHGF_RI_OBJECTIVE_RECORD_TYPE].recordTypeId
        break
      default:
        objectiveRecordType = null
        break
    }
  }
  if (!objectiveRecordType) {
    console.error('No objective record type was linked for: ', fundType)
  }
  const inner = value.map(item => ({
    Outcomes__c: item.outcomes.join('\n|||\n'),
    Objective__c: item.objective,
    Identified_needs__c: item.needs,
    Id: item.id,
    RecordTypeId: objectiveRecordType,
    toBeDeleted: item.toBeDeleted
  }))
  saveMap[connectedObjectId].Objectives__r = inner
}

export const FormObjectivesDefaultValue = (obj) => {
  if (!obj || !obj.Objectives__r) {
    return []
  }

  return obj.Objectives__r.records.map((objective) => ({
    outcomes: objective.Outcomes__c
      ? (() => {
          const clean = purgeInvalidPDFSignFromString(objective.Outcomes__c)
          return clean.split('\n|||\n')
        })()
      : [''],
    objective: objective.Objective__c,
    needs: purgeInvalidPDFSignFromString(objective.Identified_needs__c),
    id: objective.Id,
    toBeDeleted: objective.toBeDeleted
  }))
}

const sizeTrans = <Trans>Must Have at least one Outcome</Trans>
const objectiveSizeTrans = <Trans>Must Have at least one Objective</Trans>
export const FormObjectivesValidation = ({ typeProps, ...props }) => {
  const { outcomesRequired, needsRequired, outcomesMaxChar, needsMaxChar } =
    typeProps

  const objectiveShape = {
    objective: Yup.string()
      .ensure()
      .required(requiredTrans)
      .label(<Trans>Objective</Trans>),
    needs: Yup.string().ensure(),
    outcomes: Yup.array()
      .ensure()
      .min(1, sizeTrans)
      .label(<Trans>Outcomes</Trans>)
  }

  if (outcomesRequired) {
    objectiveShape.outcomes = objectiveShape.outcomes.test(
      'at-least-one-required',
      sizeTrans,
      function (outcomes) {
        if (!outcomes || outcomes.length === 0) {
          return this.createError({
            path: `${this.path}[0]`,
            message: sizeTrans
          })
        }

        if (!outcomes[0] || outcomes[0].trim() === '') {
          return this.createError({
            path: `${this.path}[0]`,
            message: requiredTrans
          })
        }

        return true
      }
    )
  }

  if (needsRequired) {
    objectiveShape.needs = objectiveShape.needs
      .required(requiredTrans)
      .label(<Trans>Needs</Trans>)
  }

  if (needsMaxChar) {
    objectiveShape.needs = objectiveShape.needs.max(
      Number(needsMaxChar),
      ({ max }) =>
        myI18n?._(t`This fields length cannot exceed ${max} characters!`)
    )
  }

  if (outcomesMaxChar) {
    objectiveShape.outcomes = objectiveShape.outcomes.of(
      Yup.string()
        .ensure()
        .max(Number(outcomesMaxChar), ({ max }) =>
          myI18n?._(t`This fields length cannot exceed ${max} characters!`)
        )
    )
  }

  return Yup.array()
    .ensure()
    .min(1, objectiveSizeTrans)
    .label(<Trans>Objectives</Trans>)
    .of(Yup.object().shape(objectiveShape))
}

/**
 * Form element which allows adding and configuring project objectives. Each objective requiries selecting it type and providing objectives needs and list of outcomes. Objectives are saved as
 * FGM_Base__Objective__c child objects on connected Opportunity object.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection} connectedObject Connected Salesforce object of type Opportunity.
 * @param  {string[]} typeProps.options Array of phases defined in the form editor. For each one user will need to fill the required information.
 * @param  {number}  [typeProps.outcomesMaxChar] If provided, the number of characters that the outcomes text input fields will be restricted to.
 * @param  {number}  [typeProps.outcomesMaxWords] If provided, the number of words that the outcomes text input fields will be restricted to.
 * @param  {number}  [typeProps.outcomesRows=1] Number of rows which outcomes text input fields should occupy.
 * @param  {boolean} [typeProps.outcomesExpandable=false] If outcomes text fields should expand further below if new line is inserted.
 * @param  {number}  [typeProps.needsMaxChar] If provided, the number of characters that the needs text input field will be restricted to.
 * @param  {number}  [typeProps.needsMaxWords] If provided, the number of words that the needs text input field will be restricted to.
 * @param  {number}  [typeProps.needsRows=1] Number of rows which needs text input field should occupy.
 * @param  {boolean} [typeProps.needsExpandable=false] If needs text field should expand further below if new line is inserted.
 */
export const FormObjectives = ({
  id,
  useMultiuser,
  muBag,
  title,
  helpText,
  connectedObject,
  editMode,
  disabled,
  typeProps,
  langVersion,
  i18n = myI18n,
  saving,
  ...item
}) => {
  const {
    outcomesMaxChar,
    outcomesMaxWords,
    outcomesRows,
    outcomesExpandable,
    needsMaxChar,
    needsMaxWords,
    needsRows,
    needsExpandable,
    outcomesRequired,
    needsRequired,
    options
  } = typeProps
  const classes = useStyles()
  const user = useSelector((state) => state.user)
  const { enqueueSnackbar } = useSnackbar()
  const [indexToDelete, setIndexToDelete] = React.useState(null)
  const { values, errors, setFieldTouched } = useFormikContext()

  const isElementLocked = Object.keys(values?.muInfo).find((key) => {
    if (key.includes(id)) return true
    return false
  })

  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )
  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or connected object is not of
          "Opportunity" type!
        </Trans>
      </div>
    )
  }

  const needsHelpTextLabel = parseFormLabelText({
    text: typeProps.needsHelpText,
    i18n,
    langVersion
  })
  const outcomesHelpTextLabel = parseFormLabelText({
    text: typeProps.outcomesHelpText,
    i18n,
    langVersion
  })

  return (
    <div className={classes.root}>
      <h3>{title}</h3>
      <FormHelptext helpText={helpText} />
      <FieldArray
        name={id}
        render={({ push, form, name, remove }) => {
          const objectives = form.values[name] || []
          const alreadySelected = {}
          objectives.forEach((obj, oIndex) => {
            if (obj.objective) {
              if (alreadySelected[obj.objective]) {
                alreadySelected[obj.objective].push(oIndex)
              } else {
                alreadySelected[obj.objective] = [oIndex]
              }
            }
          })
          return (
            <div>
              <div style={{ paddingLeft: 15, paddingTop: 10 }}>
                <Button
                  variant='contained'
                  color='primary'
                  type='button'
                  disabled={disabled}
                  onClick={() => {
                    push({
                      objective: '',
                      needs: '',
                      outcomes: [''],
                      toBeDeleted: false
                    })
                    setFieldTouched(id)
                    if (useMultiuser) {
                      endEditingField({
                        ...muBag,
                        fieldId: id,
                        fieldValue: [
                          ...objectives,
                          {
                            objective: '',
                            needs: '',
                            outcomes: ['']
                          }
                        ]
                      })
                    }
                  }}
                >
                  <Icon color='inherit'>add</Icon>
                  <span>
                    <Trans>New Objective</Trans>
                  </span>
                </Button>
              </div>
              <div className='w-100 overflow-auto' style={{ padding: 20 }}>
                <Table style={{ whiteSpace: 'pre' }}>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell style={{ paddingLeft: 35 }}>
                        <b>
                          <Trans>Objectives</Trans>
                        </b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {objectives.map((item, index) => {
                      return [
                        item.toBeDeleted
                          ? null
                          : (
                            <TableRow key={index}>
                              <TableCell style={{ paddingLeft: 20 }}>
                                <Grid
                                  wrap='nowrap'
                                  container
                                  style={{
                                    paddingLeft: 15,
                                    paddingTop: 15,
                                    paddingBottom: 15
                                  }}
                                >
                                  <TextFieldWithFormik
                                    select
                                    actionIconButton={
                                      <Grid item>
                                        <IconButton
                                          disabled={disabled || isElementLocked}
                                          variant='filled'
                                          onClick={() => {
                                            setIndexToDelete(index)
                                          }}
                                          size='large'
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Grid>
                                  }
                                    disabled={disabled}
                                    name={id + `[${index}].objective`}
                                    required
                                  >
                                    {options
                                      ?.filter((option) => {
                                        return (
                                          !alreadySelected[option.value] ||
                                        alreadySelected[option.value].includes(
                                          index
                                        )
                                        )
                                      })
                                      .map((item) => {
                                        return (
                                          <MenuItem
                                            key={item.value}
                                            value={item.value}
                                          >
                                            {item.labels
                                              ? (
                                                  getLabelFromTranslationData({
                                                    data: item.labels,
                                                    langVersion
                                                  })
                                                )
                                              : (
                                                <Trans id={item.value} />
                                                )}
                                          </MenuItem>
                                        )
                                      })}
                                  </TextFieldWithFormik>
                                </Grid>
                              </TableCell>
                            </TableRow>
                            ),
                        item.toBeDeleted
                          ? null
                          : (
                            <TableRow key={index + objectives.length}>
                              <TableCell
                                style={{
                                  paddingLeft: 75,
                                  paddingTop: 25,
                                  paddingBottom: 25,
                                  paddingRight: 35
                                }}
                              >
                                {/* <Grid container direction='row' alignItems='center'>
                            <FormTopLabel required={needsRequired}>
                              <Trans>Needs</Trans>
                            </FormTopLabel>
                            <MUPreviouslySavedValuesPanel
                              disabled={disabled}
                              muBag={muBag}
                              fieldId={id + `[${index}].needs`}
                              useMultiuser={useMultiuser}
                            />
                          </Grid> */}

                                <FormTopLabel required={needsRequired}>
                                  <Trans>Needs</Trans>
                                </FormTopLabel>
                                <FormHelptext
                                  helpText={needsHelpTextLabel}
                                />
                                <TextFieldWithFormik
                                  useMultiuser={useMultiuser}
                                  muBag={muBag}
                                  name={id + `[${index}].needs`}
                                  label=''
                                  multiline
                                  minRows={needsRows || 5}
                                  maxRows={
                                  needsExpandable ? null : needsRows || 5
                                }
                                  disabled={disabled}
                                  margin='normal'
                                  required={needsRequired}
                                  limit={needsMaxChar && Number(needsMaxChar)}
                                  maxWords={
                                  needsMaxWords && Number(needsMaxWords)
                                }
                                />
                              </TableCell>
                            </TableRow>
                            ),
                        item.toBeDeleted
                          ? null
                          : (
                            <TableRow key={index + objectives.length * 2}>
                              <TableCell
                                style={{
                                  paddingLeft: 75,
                                  paddingTop: 25,
                                  paddingBottom: 25,
                                  paddingRight: 35
                                }}
                              >
                                <FieldArray
                                  name={id + `[${index}].outcomes`}
                                  render={({ push, remove }) => (
                                    <>
                                      <Grid
                                        container
                                        wrap='nowrap'
                                        direction='row'
                                        gap='24px'
                                      >
                                        <Grid item>
                                          <FormTopLabel required>
                                            <Trans>Outcomes</Trans>
                                          </FormTopLabel>
                                        </Grid>
                                        <Grid item>
                                          <Button
                                            variant='contained'
                                            color='primary'
                                            disabled={disabled}
                                            onClick={() => {
                                              push('')
                                              setFieldTouched(id)
                                              if (useMultiuser) {
                                                endEditingField({
                                                  userId: user.userId,
                                                  ...muBag,
                                                  fieldId:
                                                  id + `[${index}].outcomes`,
                                                  fieldValue: [
                                                    ...item.outcomes,
                                                    ''
                                                  ]
                                                })
                                              }
                                            }}
                                          >
                                            <Add />
                                          </Button>
                                        </Grid>
                                      </Grid>
                                      <FormHelptext
                                        helpText={outcomesHelpTextLabel}
                                      />

                                      {item.outcomes.map(
                                        (outcome, outcomeIndex) => (
                                          <TextFieldWithFormik
                                            key={outcomeIndex}
                                            actionIconButton={
                                            outcomeIndex > 0 && (
                                              <Grid item>
                                                <IconButton
                                                  variant='contained'
                                                  disabled={
                                                    disabled || isElementLocked
                                                  }
                                                  onClick={() => {
                                                    remove(outcomeIndex)
                                                    if (useMultiuser) {
                                                      const newValue = [
                                                        ...item.outcomes
                                                      ]
                                                      newValue.splice(
                                                        outcomeIndex,
                                                        1
                                                      )
                                                      endEditingField({
                                                        ...muBag,
                                                        fieldId:
                                                          id +
                                                          `[${index}].outcomes`,
                                                        fieldValue: newValue
                                                      })
                                                    }
                                                  }}
                                                  size='large'
                                                >
                                                  <Delete />
                                                </IconButton>
                                              </Grid>
                                            )
                                          }
                                            useMultiuser={useMultiuser}
                                            muBag={muBag}
                                            name={
                                            id +
                                            `[${index}].outcomes[${outcomeIndex}]`
                                          }
                                            label=''
                                            multiline
                                            minRows={outcomesRows || 2}
                                            maxRows={
                                            outcomesExpandable
                                              ? null
                                              : outcomesRows || 2
                                          }
                                            disabled={disabled}
                                            margin='normal'
                                            limit={
                                            outcomesMaxChar &&
                                            Number(outcomesMaxChar)
                                          }
                                            maxWords={
                                            outcomesMaxWords &&
                                            Number(outcomesMaxWords)
                                          }
                                            required={
                                            outcomesRequired &&
                                            outcomeIndex === 0
                                          }
                                          />
                                        )
                                      // <Grid
                                      //   container
                                      //   spacing={2}
                                      //   key={outcomeIndex}
                                      //   alignItems='center'
                                      // >
                                      //   <Grid item xs>
                                      //     <TextFieldWithFormik
                                      //       actionIconButton={
                                      //             outcomeIndex > 0 && (
                                      //               <Grid item>
                                      //                 <IconButton
                                      //                   variant='contained'
                                      //                   color='secondary'
                                      //                   type='button'
                                      //                   disabled={
                                      //                     disabled ||
                                      //                     isElementLocked
                                      //                   }
                                      //                   onClick={() => {
                                      //                     remove(outcomeIndex)
                                      //                     if (useMultiuser) {
                                      //                       const newValue = [
                                      //                         ...item.outcomes
                                      //                       ]
                                      //                       newValue.splice(
                                      //                         outcomeIndex,
                                      //                         1
                                      //                       )
                                      //                       endEditingField({
                                      //                         ...muBag,
                                      //                         fieldId:
                                      //                           id +
                                      //                           `[${index}].outcomes`,
                                      //                         fieldValue: newValue
                                      //                       })
                                      //                     }
                                      //                   }}
                                      //                   size='large'
                                      //                 >
                                      //                   <Icon
                                      //                     color={
                                      //                       disabled ||
                                      //                       isElementLocked
                                      //                         ? 'inherit'
                                      //                         : 'error'
                                      //                     }
                                      //                   >
                                      //                     delete
                                      //                   </Icon>
                                      //                 </IconButton>
                                      //               </Grid>
                                      //             )
                                      //           }
                                      //       useMultiuser={useMultiuser}
                                      //       muBag={muBag}
                                      //       name={
                                      //             id +
                                      //             `[${index}].outcomes[${outcomeIndex}]`
                                      //           }
                                      //       label=''
                                      //       multiline
                                      //       minRows={outcomesRows || 2}
                                      //       maxRows={
                                      //             outcomesExpandable
                                      //               ? null
                                      //               : outcomesRows || 2
                                      //           }
                                      //       disabled={disabled}
                                      //       margin='normal'
                                      //       limit={
                                      //             outcomesMaxChar &&
                                      //             Number(outcomesMaxChar)
                                      //           }
                                      //       maxWords={
                                      //             outcomesMaxWords &&
                                      //             Number(outcomesMaxWords)
                                      //           }
                                      //       required={
                                      //             outcomesRequired &&
                                      //             outcomeIndex === 0
                                      //           }
                                      //     />
                                      //   </Grid>
                                      //   <Grid item>
                                      //     <MUPreviouslySavedValuesPanel
                                      //       disabled={disabled}
                                      //       muBag={muBag}
                                      //       fieldId={
                                      //             id +
                                      //             `[${index}].outcomes[${outcomeIndex}]`
                                      //           }
                                      //       useMultiuser={useMultiuser}
                                      //     />
                                      //   </Grid>
                                      // </Grid>
                                      )}
                                    </>
                                  )}
                                />
                              </TableCell>
                            </TableRow>
                            )
                      ]
                    })}
                  </TableBody>
                </Table>
              </div>
              <FormErrorLabel error={Boolean(_.get(errors, `${id}`))} id={id} />
              <Dialog
                fullWidth
                maxWidth='xs'
                open={Boolean(indexToDelete) || indexToDelete === 0}
                onClose={() => {
                  setIndexToDelete(null)
                }}
                aria-labelledby='alert-dialog-slide-title'
                aria-describedby='alert-dialog-slide-description'
              >
                <DialogTitleWithIconClose
                  handleClose={() => {
                    setIndexToDelete(null)
                  }}
                  label={
                    <Trans>
                      Are you sure you want to delete this objective?
                    </Trans>
                  }
                />

                <DialogContent>
                  <DialogContentText>
                    <Trans>This process cannot be undone.</Trans>
                  </DialogContentText>

                  <Grid container direction='column' gap='16px'>
                    <Button
                      fullWidth
                      onClick={() => {
                        const toDelete = objectives[indexToDelete]
                        const modalId = toDelete.id

                        if (modalId) {
                          enqueueSnackbar(<Trans>Deleted</Trans>, {
                            variant: 'success'
                          })

                          const newObjectives = [...objectives]
                          newObjectives[indexToDelete] = {
                            ...newObjectives[indexToDelete],
                            toBeDeleted: true
                          }
                          form.setFieldValue(name, newObjectives)

                          if (useMultiuser) {
                            const newValue = [...objectives]
                            newValue[indexToDelete] = {
                              ...newValue[indexToDelete],
                              toBeDeleted: true
                            }
                            endEditingField({
                              ...muBag,
                              fieldId: id,
                              fieldValue: newValue
                            })
                          }
                        } else {
                          remove(indexToDelete)
                        }
                        setIndexToDelete(null)
                      }}
                      variant='contained'
                      color='primary'
                    >
                      <Trans>Yes, delete it forever</Trans>
                    </Button>
                    <Button
                      onClick={() => {
                        setIndexToDelete(null)
                      }}
                      fullWidth
                      variant='outlined'
                    >
                      <Trans>Cancel</Trans>
                    </Button>
                  </Grid>
                </DialogContent>
              </Dialog>
            </div>
          )
        }}
      />
    </div>
  )
}

/** Helper component for printing objectives
 * @param {Object} data - Object containing objectives data.
 * @param {number} index - Index of the objective.
 * @returns {JSX.Element}
 */
export const Objective = ({ data, index }) => {
  const { objective, needs, outcomes = [] } = data
  return (
    <div key={index} style={{ width: '100%', padding: 10, marginTop: 15 }}>
      <div style={{ padding: 8 }}>
        <Typography className='form-print-subtitle'>
          <Trans>Objective</Trans>
        </Typography>
        <Typography className='form-read-only-text'>
          <Trans id={objective} />
        </Typography>
      </div>
      <Divider />
      <div style={{ padding: 8 }}>
        <Typography className='form-print-subtitle'>
          <Trans>Needs</Trans>
        </Typography>
        <Typography className='form-read-only-text'>{needs}</Typography>
      </div>
      <Divider />
      <div style={{ padding: 8 }}>
        <Typography className='form-print-subtitle'>
          <Trans>Outcomes</Trans>
        </Typography>
        <List>
          {Boolean(outcomes.length === 0) && (
            <ListItem style={{ display: 'flex' }}>
              <Trans>This list is empty!</Trans>
            </ListItem>
          )}
          {outcomes.map((item, index) => {
            return (
              <ListItem key={index} style={{ display: 'flex' }}>
                <ListItemIcon>
                  <Icon>fiber_manual_record</Icon>
                </ListItemIcon>

                <Typography className='form-read-only-text'>
                  <Trans id={item} />
                </Typography>
              </ListItem>
            )
          })}
        </List>
      </div>
    </div>
  )
}

export const formObjectivesSavePromise = async ({ value }) => {
  const objectivesToDelete = value.filter((obj) => obj.toBeDeleted)

  const deletePromises = objectivesToDelete.map(async (o) => {
    try {
      await deleteObjective(o.id)
    } catch (error) {
      console.error(`error deleting objective ${o.id}:`, error)
      throw error
    }
  })

  const promisesToExecute = [
    ...deletePromises.length > 0 ? deletePromises : [Promise.resolve()]
  ]

  return {
    promise: Promise.all(promisesToExecute)
  }
}

export const objectiveTranslations = {
  'Leverage existing funding': msg`Leverage existing funding`,
  'Identify and address organizational and/or sectoral gaps in capacity': msg`Identify and address organizational and/or sectoral gaps in capacity`,
  'Support and strengthen organizational transformation and growth': msg`Support and strengthen organizational transformation and growth`,
  'Develop resources related to improving organizational and/or sectoral capacity': msg`Develop resources related to improving organizational and/or sectoral capacity`,
  'Develop resources related to construction of new affordable housing supply': msg`Develop resources related to construction of new affordable housing supply`,
  'Address systemic barriers to housing': msg`Address systemic barriers to housing`,
  'Improve sectoral ability to leverage existing funding': msg`Improve sectoral ability to leverage existing funding`,
  'Address funding challenges faced by service providers': msg`Address funding challenges faced by service providers`,
  'Advance projects along the housing development process': msg`Advance projects along the housing development process`,
  'Increase supply of affordable housing units': msg`Increase supply of affordable housing units`,
  'Promote sectoral innovation, education, and knowledge-transfer': msg`Promote sectoral innovation, education, and knowledge-transfer`,
  'Develop tools, materials, and innovative solutions and make them more accessible': msg`Develop tools, materials, and innovative solutions and make them more accessible`,
  'Gather data to inform development of housing strategies and/or to measure sectoral progress': msg`Gather data to inform development of housing strategies and/or to measure sectoral progress`,
  'Increase/promote access to information': msg`Increase/promote access to information`,
  'Build capacity around housing decisions and responsibilities': msg`Build capacity around housing decisions and responsibilities`,
  'Increase participation in housing-related decisions and projects': msg`Increase participation in housing-related decisions and projects`,
  'Lead the sector to reduce its environmental footprint': msg`Lead the sector to reduce its environmental footprint`,
  'Develop tools and make them more accessible': msg`Develop tools and make them more accessible`,
  'Support sectoral consolidation and other partnerships': msg`Support sectoral consolidation and other partnerships`,
  'Reinforce organizational capacity': msg`Reinforce organizational capacity`,
  'Enhance sector resilience': msg`Enhance sector resilience`,
  'Fill sectoral gaps in housing services': msg`Fill sectoral gaps in housing services`,
  'Encourage housing initiative for Indigenous communities': msg`Encourage housing initiative for Indigenous communities`,
  'Engage community': msg`Engage community`,
  'Address the needs of underrepresented and marginalized communities': msg`Address the needs of underrepresented and marginalized communities`,
  'Build internal organizational capacity to undertake more housing development projects': msg`Build internal organizational capacity to undertake more housing development projects`,
  'Improve organizational ability to leverage existing funding': msg`Improve organizational ability to leverage existing funding`,
  'Develop shareable resources related to construction of new affordable housing supply': msg`Develop shareable resources related to construction of new affordable housing supply`,
  'Lower greenhouse gas emissions and energy use': msg`Lower greenhouse gas emissions and energy use`,
  'Enhance indoor air quality': msg`Enhance indoor air quality`,
  'Conserve water': msg`Conserve water`,
  "Strengthen the organization's climate resilience": msg`Strengthen the organization's climate resilience`,
  'Foster sustainable, community-driven green spaces': msg`Foster sustainable, community-driven green spaces`,
  'Encourage sustainable waste management practices among tenants': msg`Encourage sustainable waste management practices among tenants`,
  'Facilitate active transportation options': msg`Facilitate active transportation options`,
  'Support long-term sustainability initiatives and planning': msg`Support long-term sustainability initiatives and planning`
}

export const formObjectivesValueToExportText = (value, _) => {
  if (!value || !Array.isArray(value)) {
    return ''
  }

  return value.map((item, index) => {
    const { objective, outcomes, needs } = item

    const translation = objectiveTranslations[objective]
    const objectiveString = translation ? i18n._(translation) : objective || ''
    const needsString = `${i18n._(t`Needs`)}:\n${needs}`
    const outcomesString = `${i18n._(t`Outcomes`)}:\n${Array.isArray(outcomes) ? outcomes.join('\n\n') : outcomes}`

    return `${index + 1}. ${objectiveString}\n\n${needsString}\n\n${outcomesString}`
  }).join('\n\n')
}
